/**
 * The find box shared by the two surfaces that list loads: the dispatch board
 * (pages/Dispatch/LoadList.vue) and billing (pages/Billing/BillingList.vue).
 *
 * A MIXIN, not twenty duplicated lines, for one reason: a dispatcher does not
 * think of these as two features. If the box on Billing matched fewer fields
 * than the box on Dispatch, or debounced differently, or said something else
 * when nothing matched, that difference would read as one of the two being
 * broken. The matching itself is not here at all — it is the API's `q`
 * parameter (LoadController.applyBoardSearch), so there is exactly one answer
 * to "what does this search" for every list in the product.
 *
 * WHY SERVER-SIDE, when Billing already holds all its rows. Billing's old find
 * box filtered the rows it had, and matched `String(load.id)` and nothing
 * else — so typing a customer's name into a table with a Customer column
 * returned nothing. The dispatch board could not use that approach at all: it
 * holds ONE page of 25, so a local filter could only ever search the page a
 * dispatcher happens to be looking at, which is the failure that looks most
 * like success. One server-side implementation is the only version of this
 * that is honest on both.
 *
 * A component using this mixin must implement `onSearchChanged()` — what to do
 * once the typing settles — and spread `searchParams` into its own request.
 */

/**
 * Long enough that ordinary typing sends one request rather than one per
 * keystroke, short enough that the table has moved by the time someone looks
 * up from the keyboard.
 */
export const SEARCH_DEBOUNCE_MS = 250;

export const loadSearchMixin = {
  data() {
    return {
      searchQuery: '',
      // The pending debounce, held so beforeUnmount can cancel it: a timer
      // that fires after the component is gone calls loadData on an instance
      // Vue has already torn down.
      searchTimer: null,
      // What the ROWS ON SCREEN were fetched for, which is not always what is
      // in the box — someone mid-edit has typed past the last request. The
      // empty state reads this rather than searchQuery, so it never names a
      // term the server was not asked about.
      appliedSearch: '',
    };
  },
  computed: {
    /**
     * Spread into the request. Absent rather than empty when there is no
     * search: `q=''` and no `q` mean the same thing to the API, but only one
     * of them says so in a network log.
     */
    searchParams() {
      const term = this.searchQuery.trim();
      return term ? { q: term } : {};
    },
    /**
     * A list that is empty because the SEARCH matched nothing must not claim
     * the list itself is empty. "No loads in progress" under a search for a
     * misremembered unit number is a false statement about the org's data
     * when it is only a fact about the term.
     */
    displayEmptyTitle() {
      return this.appliedSearch ? `No loads match "${this.appliedSearch}"` : this.emptyTitle;
    },
    displayEmptyBody() {
      return this.appliedSearch
        ? 'Search matches the order number, PO, customer reference, customer, driver, truck, trailer and stop names.'
        : this.emptyBody;
    },
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    handleSearchInput(value) {
      this.searchQuery = value === null || value === undefined ? '' : String(value);
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.onSearchChanged();
      }, SEARCH_DEBOUNCE_MS);
    },
    /**
     * Called by the component's own fetch, at the moment it commits to a
     * request, so the empty state describes the term that was actually asked
     * about rather than whatever has been typed since.
     */
    markSearchApplied() {
      this.appliedSearch = this.searchQuery.trim();
    },
  },
};
